import ConversationItemType from 'models/conversation_item_type';
import getNormalizedEmailAddress from 'scripts/lib/get_normalized_email_address';
import { MessageChannelType } from 'models/conversation_message';
import { normalizePhoneNumber } from 'models/phone_number';

/**
 * Extracts the normalized endpoint addresses associated with a (loaded)
 * conversation item, so they can be compared against the selected address in
 * the timeline filter.
 *
 * Scope: plain-string channels only (email, SMS, phone, voicemail, and
 * WhatsApp). Social channels (Twitter / Instagram / Facebook) store opaque
 * IDs that require profile / channel-config resolution and are intentionally
 * not handled here — items on those channels return no addresses and are
 * therefore hidden whenever an address filter is active.
 *
 * @param {Immutable.Map} itemImmutable a conversation item from the history store
 * @returns {string[]} normalized addresses (lowercased emails / normalized phones)
 */
export default function getItemAddresses(itemImmutable) {
  const content = itemImmutable && itemImmutable.get('content');
  if (!content) {
    return [];
  }

  switch (content.get('type')) {
    case ConversationItemType.EMAIL:
    case ConversationItemType.EMAIL_CAMPAIGN_OUTREACH:
      return emailAddresses(content);
    case ConversationItemType.SMS:
    case ConversationItemType.SMS_CAMPAIGN_OUTREACH:
      return phoneAddresses([content.get('from'), content.get('to')]);
    case ConversationItemType.PHONE_CALL:
    case ConversationItemType.ABANDONED_CALL:
    case ConversationItemType.VOICEMAIL:
      return phoneAddresses([content.get('companyNumber'), content.get('customerNumber')]);
    case ConversationItemType.CONVERSATION_MESSAGE:
      if (content.get('channel') === MessageChannelType.WHATSAPP) {
        return phoneAddresses([content.get('companyAddress'), content.get('customerAddress')]);
      }
      return [];
    default:
      return [];
  }
}

function emailAddresses(content) {
  const addresses = [];
  const from = content.get('from');
  if (from) {
    addresses.push(from);
  }
  ['to', 'cc', 'bcc'].forEach(field => {
    const list = content.get(field);
    if (list) {
      list.forEach(address => addresses.push(address));
    }
  });
  return addresses.map(address => getNormalizedEmailAddress(address)).filter(Boolean);
}

function phoneAddresses(numbers) {
  return numbers.map(safeNormalizePhoneNumber).filter(Boolean);
}

function safeNormalizePhoneNumber(number) {
  if (!number) {
    return null;
  }
  try {
    return normalizePhoneNumber(number);
  } catch (e) {
    return null;
  }
}
